import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { FaBars, FaTimes, FaPhone } from 'react-icons/fa';
import logo from '../ubotika-logo.png';
import './Navigation.css';

const scrollItems = [
  { id: 'hero', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'services', label: 'Services' },
  { id: 'calculator', label: 'Calculator' },
  { id: 'faq', label: 'FAQ' },
  { id: 'contact', label: 'Contact' },
];

function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const location = useLocation();
  // Treat existing slash and non-slash URLs alike without changing navigation URLs.
  const activePath = location.pathname.replace(/\/+$/, '') || '/';
  const navigate = useNavigate();

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const toggle = toggleRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    menuRef.current?.querySelector('button, a')?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsMobileMenuOpen(false);
      if (event.key !== 'Tab') return;
      const items = [toggleRef.current, ...menuRef.current.querySelectorAll('button, a[href]')].filter(Boolean);
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const onResize = () => { if (window.innerWidth > 1100) setIsMobileMenuOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
      toggle?.focus();
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      if (location.pathname !== '/') return;

      // Determine active section
      const sections = scrollItems
        .map(item => document.getElementById(item.id))
        .filter(Boolean)
        .sort((a, b) => a.offsetTop - b.offsetTop);
      const scrollPosition = window.scrollY + 150;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const scrollToSection = (sectionId) => {
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: reduceMotion ? 'instant' : 'smooth' });
      }
    } else {
      navigate('/', { state: { scrollTo: sectionId } });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        className={`navigation ${isScrolled ? 'scrolled' : ''}`}
        aria-label="Main navigation"
        initial={reduceMotion ? false : { y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="nav-container">
          {/* Logo */}
          <button
            className="nav-logo"
            onClick={() => scrollToSection('hero')}
            aria-label="UBOTIKA home"
          >
            <img src={logo} alt="UBOTIKA" className="nav-logo-image" />
            <span className="nav-wordmark" aria-hidden="true">UBOTIKA</span>
          </button>

          {/* Desktop Navigation */}
          <div className="nav-links">
            {scrollItems.filter((item) => ['about', 'services', 'calculator'].includes(item.id)).map((item) => (
              <button
                key={item.id}
                className={`nav-link ${activeSection === item.id && location.pathname === '/' ? 'active' : ''}`}
                onClick={() => scrollToSection(item.id)}
              >
                {item.label}
                {activeSection === item.id && location.pathname === '/' && (
                  <motion.div
                    className="nav-indicator"
                    layoutId="navIndicator"
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}
              </button>
            ))}
            <Link
              to="/ai-agents-los-angeles"
              className={`nav-link nav-link-secondary ${activePath === '/ai-agents-los-angeles' ? 'active' : ''}`}
            >
              AI Agency LA
              {activePath === '/ai-agents-los-angeles' && (
                <motion.div
                  className="nav-indicator"
                  layoutId="navIndicator"
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}
            </Link>
            <Link
              to="/web-apps"
              className={`nav-link ${activePath === '/web-apps' ? 'active' : ''}`}
            >
              Web Apps
              {activePath === '/web-apps' && (
                <motion.div
                  className="nav-indicator"
                  layoutId="navIndicator"
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}
            </Link>
            <Link
              to="/pricing"
              className={`nav-link ${activePath === '/pricing' ? 'active' : ''}`}
            >
              Pricing
              {activePath === '/pricing' && (
                <motion.div
                  className="nav-indicator"
                  layoutId="navIndicator"
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}
            </Link>
            <Link
              to="/blog"
              className={`nav-link ${location.pathname.startsWith('/blog') ? 'active' : ''}`}
            >
              Blog
              {location.pathname.startsWith('/blog') && (
                <motion.div
                  className="nav-indicator"
                  layoutId="navIndicator"
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}
            </Link>
          </div>

          {/* Phone & CTA */}
          <a href="tel:+14244344541" className="nav-phone" aria-label="Call our AI receptionist demo line: (424) 434-4541">
            <FaPhone className="nav-phone-icon" />
            <span>AI demo: (424) 434-4541</span>
          </a>
          <Link
            to="/free-missed-lead-audit"
            className="nav-cta"
          >
            Free Audit
          </Link>

          {/* Mobile Menu Toggle */}
          <button
            ref={toggleRef}
            className="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              className="mobile-menu-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              className="mobile-menu"
              id="mobile-navigation"
              ref={menuRef}
              aria-label="Mobile navigation"
              initial={reduceMotion ? false : { x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: reduceMotion ? 0 : 0.3 }}
            >
              <div className="mobile-menu-content">
                <span className="mobile-menu-label">Explore UBOTIKA</span>
                {scrollItems.map((item, index) => (
                  <motion.button
                    key={item.id}
                    className={`mobile-nav-link ${activeSection === item.id && location.pathname === '/' ? 'active' : ''}`}
                    onClick={() => scrollToSection(item.id)}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {item.label}
                  </motion.button>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: scrollItems.length * 0.1 }}
                >
                  <Link
                    to="/ai-agents-los-angeles"
                    className={`mobile-nav-link ${activePath === '/ai-agents-los-angeles' ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    AI Agency LA
                  </Link>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (scrollItems.length + 1) * 0.1 }}
                >
                  <Link
                    to="/blog"
                    className={`mobile-nav-link ${location.pathname.startsWith('/blog') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Blog
                  </Link>
                </motion.div>
                <motion.a
                  href="tel:+14244344541"
                  className="mobile-nav-phone"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (scrollItems.length + 2) * 0.1 }}
                >
                  <FaPhone style={{ marginRight: '8px' }} />
                  AI demo: (424) 434-4541
                </motion.a>
                <Link to="/web-apps" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>Web Apps</Link>
                <Link to="/pricing" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>Pricing</Link>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (scrollItems.length + 3) * 0.1 }}
                >
                  <Link
                    to="/free-missed-lead-audit"
                    className="mobile-nav-cta"
                    onClick={() => setIsMobileMenuOpen(false)}
                    style={{ display: 'inline-block', textDecoration: 'none' }}
                  >
                    Free Audit
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navigation;
